((environment, createGalleryHelpers) => {
  const gallery = createGalleryHelpers();
  if (typeof module === 'object' && module.exports) module.exports = gallery;
  if (!environment?.document) return;
  if (window.AlawooderProduct) { window.AlawooderProduct.mountAll(); window.AlawooderProduct.refreshAll(); return; }
  const instances = new Map();
  function init(root) {
    if (!root || root.dataset.awReady) return;
    const dataNode = root.querySelector('[data-aw-variant-data]');
    if (!dataNode) return;
    const data = JSON.parse(dataNode.textContent);
    const copy = JSON.parse(root.querySelector('[data-aw-product-copy]')?.textContent || '{}');
    const label = (key, fallback) => copy[key] || fallback;
    const controller = new AbortController();
    const cleanups = [];
    const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, {...options, signal: controller.signal});
    const form = root.querySelector('[data-aw-product-form]');
    if (!form) return;
    const select = form.querySelector('[data-aw-variant-select]');
    const quantity = form.querySelector('[data-aw-quantity]');
    const track = root.querySelector('[data-aw-gallery-track]');
    if (!select || !quantity || !track) return;
    root.dataset.awReady = 'true';
    const canonicalSlides = [...track.querySelectorAll('[data-aw-media]')];
    const canonicalThumbs = [...root.querySelectorAll('[data-aw-thumb]')];
    const mediaRecords = canonicalSlides.map(slide => ({
      id: slide.dataset.awMedia,
      colors: slide.dataset.awMediaColors,
      shared: slide.dataset.awMediaShared === 'true',
      exclude: slide.dataset.awMediaExclude === 'true',
      preferredFinish: slide.dataset.awPreferredFinish,
      slide
    }));
    const thumbsByMedia = new Map(canonicalThumbs.map(thumb => [thumb.dataset.awThumb, thumb]));
    const originalThumbLabels = new Map(canonicalThumbs.map(thumb => [thumb.dataset.awThumb, thumb.querySelector('.aw-pdp-thumb-label')?.textContent.trim() || 'Product photograph']));
    let slides = [...canonicalSlides];
    let thumbs = [...canonicalThumbs];
    const thumbRail = root.querySelector('.aw-pdp-thumbs');
    let variant = data.variants.find(v => String(v.id) === select.value);
    let active = 0;
    let raf = 0;
    let resizeFrame = 0;
    let reorderFrame = 0;
    let pendingNavigation = null;
    const money = cents => window.AWTheme ? window.AWTheme.formatMoney(cents) : new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:window.Alawooder?.currency || 'USD',minimumFractionDigits:Number(cents) % 100 ? 2 : 0,maximumFractionDigits:2}).format(cents / 100);
    const slideTo = (index, instant = false) => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(reorderFrame);
      raf = 0;
      reorderFrame = 0;
      active = Math.max(0, Math.min(slides.length - 1, index));
      const left = track.clientWidth * active;
      const immediate = instant || matchMedia('(prefers-reduced-motion: reduce)').matches;
      pendingNavigation = !immediate && Math.abs(track.scrollLeft - left) > 1 ? active : null;
      track.scrollTo({left,behavior:immediate ? 'instant' : 'smooth'});
      updateGallery(true);
    };
    function updateGallery(revealThumb = false) {
      const total = String(slides.length).padStart(2,'0');
      const current = String(slides.length ? active + 1 : 0).padStart(2,'0');
      root.querySelector('[data-aw-gallery-count]').textContent = `${current} / ${total}`;
      root.querySelector('[data-aw-plate-number]').textContent = `${label('plate_label','Plate')} ${current} / ${total}`;
      root.querySelector('[data-aw-gallery-caption]').textContent = slides[active]?.dataset.awCaption || '';
      thumbs.forEach((thumb,index) => thumb.setAttribute('aria-current', String(index === active)));
      root.querySelectorAll('[data-aw-gallery-prev]').forEach(button => button.disabled = active <= 0);
      root.querySelectorAll('[data-aw-gallery-next]').forEach(button => button.disabled = active >= slides.length - 1);
      slides.forEach((slide,index) => {
        if (index !== active) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      if (revealThumb && thumbs[active]) {
        const thumb = thumbs[active];
        const vertical = matchMedia('(min-width: 981px)').matches;
        const thumbBounds = thumb.getBoundingClientRect();
        const railBounds = thumbRail.getBoundingClientRect();
        const position = vertical ? thumbRail.scrollTop : thumbRail.scrollLeft;
        const viewport = vertical ? thumbRail.clientHeight : thumbRail.clientWidth;
        const size = vertical ? thumb.offsetHeight : thumb.offsetWidth;
        const start = vertical ? thumbBounds.top - railBounds.top + position - thumbRail.clientTop : thumbBounds.left - railBounds.left + position - thumbRail.clientLeft;
        const outside = start < position || start + size > position + viewport;
        const crossAxisPosition = vertical ? thumbRail.scrollLeft : thumbRail.scrollTop;
        if (outside || crossAxisPosition) {
          const destination = outside ? Math.max(0,start - (viewport - size)/2) : position;
          thumbRail.scrollTo({left:vertical ? 0 : destination,top:vertical ? destination : 0,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        }
      }
      root.dispatchEvent(new CustomEvent('aw:gallery:change',{detail:{index:active,mediaId:slides[active]?.dataset.awMedia}}));
    }
    canonicalThumbs.forEach(thumb => on(thumb, 'click', () => {
      const index = slides.findIndex(slide => slide.dataset.awMedia === thumb.dataset.awThumb);
      if (index >= 0) slideTo(index);
    }));
    on(root, 'aw:gallery:select', event => {
      const index = slides.findIndex(slide => slide.dataset.awMedia === String(event.detail?.mediaId));
      if (index >= 0) slideTo(index,event.detail?.instant === true);
    });
    root.querySelectorAll('[data-aw-gallery-prev]').forEach(button => on(button, 'click', () => slideTo(active - 1)));
    root.querySelectorAll('[data-aw-gallery-next]').forEach(button => on(button, 'click', () => slideTo(active + 1)));
    on(track, 'scroll', () => {
      if (reorderFrame) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (!track.clientWidth) return;
        // A smooth programmatic move passes other slides; keep the requested piece selected.
        if (pendingNavigation !== null) {
          if (Math.abs(track.scrollLeft - track.clientWidth * pendingNavigation) > 1) return;
          active = pendingNavigation;
          pendingNavigation = null;
        }
        active = Math.max(0,Math.min(slides.length - 1,Math.round(track.scrollLeft / track.clientWidth)));
        updateGallery();
      });
    }, {passive:true});
    on(track, 'scrollend', () => {
      if (reorderFrame || pendingNavigation === null) return;
      const destination = pendingNavigation;
      pendingNavigation = null;
      slideTo(destination,true);
    });
    const interruptNavigation = () => {
      if (pendingNavigation === null) return;
      pendingNavigation = null;
      cancelAnimationFrame(raf);
      raf = 0;
      track.scrollTo({left:track.scrollLeft,behavior:'instant'});
      if (track.clientWidth) active = Math.max(0,Math.min(slides.length - 1,Math.round(track.scrollLeft / track.clientWidth)));
      updateGallery(true);
    };
    on(track, 'pointerdown',interruptNavigation,{passive:true});
    on(track, 'wheel',interruptNavigation,{passive:true});
    on(track, 'keydown', e => {
      if (e.target !== track) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {e.preventDefault();slideTo(active + (e.key === 'ArrowRight' ? 1 : -1));}
    });
    function currentFinish() {
      const colorIndex = gallery.optionIndex(root.dataset.awColorOptionIndex);
      // Older pages did not identify the Color option; retain their existing finish labels.
      const index = colorIndex === null ? 0 : colorIndex;
      return form.querySelector(`[data-aw-option-index="${index}"] input:checked`)?.value || gallery.finishAt(variant?.options, index);
    }
    function currentGalleryPlan() {
      return gallery.selectMedia(mediaRecords, {
        enabled: root.dataset.awColorFilter === 'true' && gallery.optionIndex(root.dataset.awColorOptionIndex) !== null,
        color: currentFinish(),
        nativeMediaId: variant?.featured_media_id
      });
    }
    function putVariantImageFirst(plan = currentGalleryPlan()) {
      const first = plan.media[0]?.slide;
      const viewer = root.querySelector('[data-aw-image-viewer]');
      // The viewer reads the current track on each open; close any old-color snapshot first.
      if (viewer?.open) {
        if (typeof viewer.close === 'function') viewer.close();
        else { viewer.removeAttribute('open'); viewer.dispatchEvent(new Event('close')); }
      }
      pendingNavigation = null;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizeFrame);
      cancelAnimationFrame(reorderFrame);
      track.scrollTo({left:0,top:0,behavior:'instant'});
      thumbRail.scrollTo({left:0,top:0,behavior:'instant'});
      canonicalSlides.forEach(slide => slide.querySelectorAll('video, audio').forEach(media => media.pause()));
      slides = plan.media.map(record => record.slide);
      thumbs = slides.map(slide => thumbsByMedia.get(slide.dataset.awMedia)).filter(Boolean);
      gallery.setGalleryVisibility(canonicalSlides, canonicalThumbs, slides, thumbs);
      track.replaceChildren(...slides);
      thumbRail.replaceChildren(...thumbs);
      slides.forEach((slide,index) => slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`));
      thumbs.forEach((thumb,index) => {
        const original = originalThumbLabels.get(thumb.dataset.awThumb);
        const description = original.replace(/^\d+\s*\/\s*/, '');
        thumb.setAttribute('aria-label', `View image ${index + 1}: ${description}`);
        const label = thumb.querySelector('.aw-pdp-thumb-label');
        if (label && /^\d+\s*\//.test(original)) label.textContent = `${String(index + 1).padStart(2,'0')} / ${description}`;
      });
      const image = first?.querySelector('img');
      if (image) { image.loading = 'eager'; image.fetchPriority = 'high'; }
      const thumbnail = thumbs[0]?.querySelector('img');
      if (thumbnail) thumbnail.loading = 'eager';
      active = 0;
      // Reordering the existing nodes preserves native video and model controls.
      track.scrollTo({left:0,top:0,behavior:'instant'});
      thumbRail.scrollTo({left:0,top:0,behavior:'instant'});
      updateGallery();
      // Finish canceling an in-flight smooth scroll after the reordered nodes have painted.
      reorderFrame = requestAnimationFrame(() => {
        reorderFrame = 0;
        active = 0;
        track.scrollTo({left:0,top:0,behavior:'instant'});
        thumbRail.scrollTo({left:0,top:0,behavior:'instant'});
        updateGallery();
      });
    }
    function syncQuantity() {
      const minimum = variant?.min || 1;
      const increment = variant?.increment || 1;
      const previous = Number(quantity.value) || minimum;
      const values = Array.from({length:5},(_,index) => minimum + index * increment).filter(value => !variant?.max || value <= variant.max);
      quantity.replaceChildren(...values.map(value => {
        const option = document.createElement('option');
        option.value = String(value);
        option.textContent = String(value);
        return option;
      }));
      quantity.value = String(values.includes(previous) ? previous : minimum);
    }
    function refreshJumps() {
      const modules = [...document.querySelectorAll('[data-aw-product-reference]')].filter(node => node.dataset.awProductReference === root.dataset.productId);
      root.querySelectorAll('[data-aw-pdp-jump]').forEach(link => {
        const kind = link.dataset.awPdpJump;
        const module = modules.find(node => node.dataset.awPdpKind === kind && node.getClientRects().length);
        const target = module?.querySelector(kind === 'dimensions' ? '.aw-ed-dimensions[id]' : '.aw-ed-answers[id]');
        link.hidden = !target;
        if (target) link.setAttribute('href',`#${target.id}`);
      });
    }
    function renderVariant(changeImage, replaceUrl) {
      const available = Boolean(variant?.available);
      const amount = Number(quantity.value) || variant?.min || 1;
      const total = variant ? money(variant.price * amount) : '—';
      root.querySelectorAll('[data-aw-add]').forEach(button => {
        button.dataset.awAvailable = String(available);
        button.disabled = !available || form.dataset.awBusy === 'true';
        if (!available) button.textContent = variant ? label('sold_out_label','Sold out') : label('unavailable_label','Unavailable');
        else if (button.hasAttribute('data-aw-add-with-total')) {
          button.replaceChildren(document.createTextNode(`${label('add_label','Add to bag')} · ${total}`));
          const arrow = document.createElement('span');
          arrow.setAttribute('aria-hidden','true');
          arrow.textContent = '→';
          button.appendChild(arrow);
        } else button.textContent = label('add_label','Add to bag');
      });
      root.querySelectorAll('[data-aw-price]').forEach(node => node.textContent = variant ? money(variant.price) : '—');
      root.querySelectorAll('[data-aw-compare]').forEach(compare => {
        compare.hidden = !variant || variant.compare_at_price <= variant.price;
        compare.textContent = variant ? money(variant.compare_at_price) : '';
      });
      root.querySelectorAll('[data-aw-total]').forEach(node => node.textContent = total);
      root.querySelector('[data-aw-fixed-title]').textContent = `${variant?.title || label('configure_label','Choose a finish')} · ${label('quantity_short_label','Qty')} ${amount}`;
      root.querySelector('[data-aw-stock]').textContent = available ? '' : variant ? label('stock_sold_copy','This finish is currently sold out.') : label('stock_unavailable_copy','This combination is unavailable.');
      root.querySelectorAll('[data-aw-option-index]').forEach(fieldset => {
        const checked = fieldset.querySelector('input:checked');
        fieldset.querySelector('[data-aw-option-label]').textContent = checked?.value || '';
      });
      const finish = currentFinish();
      const galleryPlan = currentGalleryPlan();
      const scopes = [root, ...document.querySelectorAll('[data-aw-product-reference]')].filter(scope => scope === root || scope.dataset.awProductReference === root.dataset.productId);
      scopes.forEach(scope => {
        gallery.syncFinishVisibility(scope, finish);
        scope.querySelectorAll('[data-aw-finish-name]').forEach(node => node.classList.toggle('aw-current-finish',gallery.normalizeColor(node.dataset.awFinishName) === gallery.normalizeColor(finish)));
        scope.querySelectorAll('[data-aw-finish-caption]').forEach(node => { node.textContent = finish; });
        if (!variant) return;
        const nativeImage = galleryPlan.media[0]?.slide.querySelector('img');
        if (nativeImage) scope.querySelectorAll('[data-aw-selected-finish-image]').forEach(image => {
          ['src','srcset','width','height','alt'].forEach(attribute => {
            const value = nativeImage.getAttribute(attribute);
            if (value === null) image.removeAttribute(attribute);
            else image.setAttribute(attribute,value);
          });
        });
      });
      refreshJumps();
      if (!variant) { if (changeImage) putVariantImageFirst(galleryPlan); return; }
      select.value = String(variant.id);
      if (changeImage) putVariantImageFirst(galleryPlan);
      if (replaceUrl) { const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url); }
    }
    on(form, 'change', e => {
      if (e.target.matches('[data-aw-option]')) {
        const selected = [...form.querySelectorAll('[data-aw-option-index]')].map(fieldset => fieldset.querySelector('input:checked')?.value);
        variant = data.variants.find(v => v.options.every((value,index) => value === selected[index]));
        syncQuantity();
        renderVariant(true,true);
      } else if (e.target === select) {
        variant = data.variants.find(v => String(v.id) === select.value);
        root.querySelectorAll('[data-aw-option]').forEach(input => { input.checked = variant?.options[Number(input.dataset.awOption)] === input.value; });
        syncQuantity();
        renderVariant(true,true);
      } else if (e.target === quantity) renderVariant(false,false);
    });
    on(form, 'aw:cart-busy', () => renderVariant(false,false));
    const alignAfterResize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        // Reposition both the slide and its thumbnail after the new layout has settled.
        slideTo(active,true);
        refreshJumps();
      });
    };
    if (window.ResizeObserver) { const observer = new ResizeObserver(alignAfterResize); observer.observe(track); cleanups.push(() => observer.disconnect()); }
    else on(window,'resize',alignAfterResize,{passive:true});
    syncQuantity();
    updateGallery();
    renderVariant(true,false);
    root.awProductRefresh = () => renderVariant(false,false);
    instances.set(root, () => {
      controller.abort();
      cleanups.forEach(cleanup => cleanup());
      [raf,resizeFrame,reorderFrame].forEach(cancelAnimationFrame);
      delete root.dataset.awReady;
      delete root.awProductRefresh;
    });
  }
  function mountAll(scope = document) {
    if (scope.matches?.('[data-aw-product]')) init(scope);
    scope.querySelectorAll('[data-aw-product]').forEach(init);
  }
  function refreshAll() { instances.forEach((_,root) => root.awProductRefresh?.()); }
  window.AlawooderProduct = {mountAll,refreshAll};
  mountAll();
  document.addEventListener('shopify:section:load', event => { mountAll(event.target); refreshAll(); });
  document.addEventListener('shopify:section:reorder',refreshAll);
  document.addEventListener('shopify:section:unload',event => {
    instances.forEach((cleanup,root) => {
      if (event.target === root || event.target.contains(root)) { cleanup(); instances.delete(root); }
    });
    requestAnimationFrame(refreshAll);
  });
})(typeof window === 'undefined' ? null : window, () => {
  function normalizeColor(value) {
    return String(value == null ? '' : value).normalize('NFKC').trim().toLowerCase().replace(/[\s_]+/g, '-');
  }
  function colorList(value) {
    return String(value == null ? '' : value).split('|').map(normalizeColor).filter(Boolean);
  }
  function optionIndex(value) {
    const text = String(value == null ? '' : value);
    return /^\d+$/.test(text) && Number.isSafeInteger(Number(text)) ? Number(text) : null;
  }
  function finishAt(options, index) {
    const resolved = optionIndex(index);
    return resolved !== null && Array.isArray(options) && options[resolved] != null ? String(options[resolved]) : '';
  }
  function selectMedia(records, options) {
    const color = normalizeColor(options.color);
    const grouped = records.some(record => colorList(record.colors).length || record.shared === true);
    const filtered = options.enabled === true && Boolean(color) && grouped;
    let eligible = records;
    if (filtered) {
      const allowed = records.filter(record => record.exclude !== true && (record.shared === true || colorList(record.colors).includes(color)));
      // Product-specific scenes precede general measurements and setup media.
      eligible = [...allowed.filter(record => record.shared !== true), ...allowed.filter(record => record.shared === true)];
    }
    const native = eligible.find(record => String(record.id) === String(options.nativeMediaId));
    const preferred = color ? eligible.find(record => normalizeColor(record.preferredFinish) === color && (!filtered || colorList(record.colors).includes(color))) : null;
    const first = native || preferred || eligible[0];
    return { media: first ? [first, ...eligible.filter(record => record !== first)] : [], filtered, color };
  }
  function setGalleryVisibility(allSlides, allThumbs, visibleSlides, visibleThumbs) {
    allSlides.forEach(slide => { slide.hidden = true; });
    allThumbs.forEach(thumb => { thumb.hidden = true; thumb.setAttribute?.('aria-current', 'false'); });
    visibleSlides.forEach(slide => { slide.hidden = false; });
    visibleThumbs.forEach(thumb => { thumb.hidden = false; });
  }
  function finishAllowed(allowed, finish) {
    const color = normalizeColor(finish);
    return Boolean(color) && colorList(allowed).includes(color);
  }
  function syncFinishVisibility(scope, finish) {
    const marked = [...scope.querySelectorAll('[data-aw-visible-finishes]')];
    if (scope.hasAttribute?.('data-aw-visible-finishes')) marked.unshift(scope);
    marked.forEach(node => { node.hidden = !finishAllowed(node.dataset.awVisibleFinishes, finish); });
  }
  return { normalizeColor, colorList, optionIndex, finishAt, selectMedia, setGalleryVisibility, finishAllowed, syncFinishVisibility };
});
